import { CheckCircle2, ChevronRight, ArrowRight, ToggleLeft, ToggleRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const PRE_LISTING_STAGE = {
  id: "pre_listing", label: "Pre-Listing", shortLabel: "Pre-List", description: "Prepare the property for market",
  nextStep: "Completing pre-listing preparations", status: "Preparing to list",
  context: "Final preparations are being made before the property is listed on the market.",
  timing: "This step typically takes 1–3 weeks"
};

const BASE_STAGES = [
  { id: "offer_negotiation", label: "Offer & Negotiation", shortLabel: "Offer/Neg", description: "Submit offers and negotiate terms",
    nextStep: "Offer submitted and terms being finalized", status: "In progress",
    context: "An offer has been submitted and buyer and seller are working through terms of the deal.",
    timing: "This stage is usually completed within a few days" },
  { id: "in_contract", label: "In Contract", shortLabel: "Contract", description: "Offer accepted, under contract",
    nextStep: "Preparing for inspection and next steps", status: "No action needed right now",
    context: "The offer has been accepted and the transaction is moving forward.",
    timing: "Next steps typically begin within a week" },
  { id: "inspection_remedy", label: "Inspection & Remedy", shortLabel: "Insp/Rem", description: "Inspections and repair negotiations",
    nextStep: "Inspection is being completed and reviewed", status: "Inspection underway",
    context: "The property is being inspected and any repair-related discussions will happen during this step.",
    timing: "This step typically takes 5–15 days",
    applicableKey: "inspection_remedy",
    naNextStep: "Proceeding to the next contract milestone", naStatus: "Not applicable to this contract",
    naContext: "Inspection and remedy terms are not part of this contract, so there is nothing you need to do for this step.",
    naTiming: null },
  { id: "appraisal", label: "Appraisal", shortLabel: "Appraisal", description: "Lender-ordered property valuation",
    nextStep: "Appraisal is being scheduled or reviewed", status: "Waiting on lender",
    context: "The lender orders an appraisal to confirm the home's value.",
    timing: "Appraisals typically take 1–2 weeks",
    applicableKey: "appraisal",
    naNextStep: "Proceeding to closing", naStatus: "Not applicable to this contract",
    naContext: "An appraisal is not needed for this transaction.",
    naTiming: null },
  { id: "closing", label: "Closing", shortLabel: "Closing", description: "Final steps to close the deal",
    nextStep: "Final steps before closing are underway", status: "Waiting on next step",
    context: "Final steps are being completed before the transaction closes.",
    timing: "Closing usually happens within 30–45 days from contract" },
  { id: "completed", label: "Completed", shortLabel: "Done", description: "Transaction complete",
    nextStep: "Transaction is complete", status: "Successfully closed",
    context: "The transaction has been successfully completed.",
    timing: null }
];

// Map legacy stage values to new ones
const normalizeStage = (stage) => {
  if (stage === "offer" || stage === "negotiation") return "offer_negotiation";
  if (stage === "inspection" || stage === "remedy") return "inspection_remedy";
  return stage;
};

export const LifecycleTracker = ({ currentStage, onStageChange, canEdit, applicability = {}, onToggleApplicable, showPreListing = false }) => {
  const STAGES = showPreListing ? [PRE_LISTING_STAGE, ...BASE_STAGES] : BASE_STAGES;
  const normalizedStage = normalizeStage(currentStage);
  const currentIndex = STAGES.findIndex(s => s.id === normalizedStage);

  const getStageStatus = (index) => {
    if (index < currentIndex) return "completed";
    if (index === currentIndex) return "active";
    return "pending";
  };

  const isStageNA = (stage) => stage.applicableKey && applicability[stage.applicableKey] === false;

  const getStageContent = (stage) => {
    if (isStageNA(stage)) {
      return {
        nextStep: stage.naNextStep,
        status: stage.naStatus,
        context: stage.naContext,
        timing: stage.naTiming,
      };
    }
    return {
      nextStep: stage.nextStep,
      status: stage.status,
      context: stage.context,
      timing: stage.timing,
    };
  };

  const currentStageData = STAGES[currentIndex];
  const currentContent = currentStageData ? getStageContent(currentStageData) : null;

  return (
    <div className="bg-white rounded-lg border border-slate-100 shadow-soft p-8" data-testid="lifecycle-tracker">
      <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-6">Timeline</h3>
      {/* Desktop View */}
      <div className="hidden md:block">
        <div className="flex items-center justify-between">
          {STAGES.map((stage, index) => {
            const status = getStageStatus(index);
            const isLast = index === STAGES.length - 1;
            const isInspRemedyNA = isStageNA(stage);
            
            return (
              <div key={stage.id} className="flex items-center flex-1">
                <div className="flex flex-col items-center relative group">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                      isInspRemedyNA && status !== "completed"
                        ? "bg-muted/60 text-muted-foreground/50 ring-1 ring-border/40"
                        : status === "completed"
                        ? "bg-emerald-500 text-white"
                        : status === "active"
                        ? "bg-primary text-primary-foreground ring-4 ring-primary/15"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {status === "completed" ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <span className="text-sm font-medium">{index + 1}</span>
                    )}
                  </div>
                  
                  <span
                    className={`mt-2 text-xs font-medium text-center ${
                      isInspRemedyNA && status !== "completed"
                        ? "text-muted-foreground/50"
                        : status === "active" ? "text-primary" : 
                        status === "completed" ? "text-foreground" : "text-muted-foreground"
                    }`}
                  >
                    {stage.label}
                  </span>
                  
                  <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-foreground text-background text-xs px-3 py-1.5 rounded-lg whitespace-nowrap z-10">
                    {isInspRemedyNA ? "Not applicable" : stage.description}
                  </div>
                </div>
                
                {!isLast && (
                  <div className="flex-1 mx-2">
                    <div
                      className={`h-0.5 rounded-full transition-all ${
                        index < currentIndex ? "bg-emerald-500" : "bg-muted"
                      }`}
                    ></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile View */}
      <div className="md:hidden">
        {/* Current stage context */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold shrink-0">
              {currentIndex + 1}
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground leading-tight">
                {currentStageData?.label}
              </p>
              <p className="text-xs text-muted-foreground leading-tight mt-0.5">
                {currentStageData && isStageNA(currentStageData)
                  ? "Not applicable"
                  : currentStageData?.description}
              </p>
            </div>
          </div>
          <span className="text-xs text-muted-foreground tabular-nums shrink-0 ml-2">
            {currentIndex + 1}/{STAGES.length}
          </span>
        </div>

        {/* Condensed horizontal step timeline */}
        <div className="flex items-center w-full" data-testid="mobile-stage-timeline">
          {STAGES.map((stage, index) => {
            const status = getStageStatus(index);
            const isLast = index === STAGES.length - 1;
            const isInspRemedyNA = isStageNA(stage);
            return (
              <div key={stage.id} className="flex items-center flex-1 min-w-0" data-testid={`mobile-stage-${stage.id}`}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 transition-all ${
                      isInspRemedyNA && status !== "completed"
                        ? "bg-muted/60 text-muted-foreground/50"
                        : status === "completed"
                        ? "bg-emerald-500 text-white"
                        : status === "active"
                        ? "bg-primary text-primary-foreground ring-[3px] ring-primary/15"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {status === "completed" ? (
                      <CheckCircle2 className="w-3 h-3" />
                    ) : (
                      <span className="text-[9px] font-semibold">{index + 1}</span>
                    )}
                  </div>
                  <span
                    className={`mt-1 text-[10px] font-medium leading-tight text-center ${
                      isInspRemedyNA && status !== "completed"
                        ? "text-muted-foreground/40"
                        : status === "active" ? "text-primary" :
                        status === "completed" ? "text-foreground" : "text-muted-foreground/70"
                    }`}
                  >
                    {stage.shortLabel}
                  </span>
                </div>
                {!isLast && (
                  <div className="flex-1 mx-0.5 -mt-3.5">
                    <div
                      className={`h-[2px] rounded-full transition-all ${
                        index < currentIndex ? "bg-emerald-500" : "bg-muted"
                      }`}
                    ></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Stage Context */}
      {currentContent && (
        <div className="mt-7 pt-5 border-t border-slate-100 space-y-3" data-testid="stage-context">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-2.5">
              <ArrowRight className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">Next Step</p>
                <p className="text-sm text-slate-800 mt-0.5 leading-snug" data-testid="next-step-text">{currentContent.nextStep}</p>
              </div>
            </div>
            <div className="flex items-start gap-2.5">
              <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                currentStageData && isStageNA(currentStageData)
                  ? "bg-slate-300" : "bg-emerald-500"
              }`}></div>
              <div>
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest">Status</p>
                <p className="text-sm text-slate-800 mt-0.5 leading-snug" data-testid="status-text">{currentContent.status}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Agent Controls */}
      {canEdit && onStageChange && (
        <div className="mt-6 pt-4 border-t border-border space-y-3">
          {/* Applicability toggles */}
          {onToggleApplicable && (
            <div className="space-y-2">
              {STAGES.filter(s => s.applicableKey).map(stage => (
                <div key={stage.id} className="flex items-center justify-between" data-testid={`${stage.id}-toggle`}>
                  <p className="text-sm text-muted-foreground">{stage.label} applies:</p>
                  <button
                    onClick={() => onToggleApplicable(stage.applicableKey)}
                    className="flex items-center gap-1.5 text-sm font-medium transition-colors"
                    data-testid={`toggle-${stage.applicableKey}-btn`}
                  >
                    {applicability[stage.applicableKey] !== false ? (
                      <>
                        <ToggleRight className="w-5 h-5 text-primary" />
                        <span className="text-primary">Yes</span>
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="w-5 h-5 text-muted-foreground" />
                        <span className="text-muted-foreground">Not applicable</span>
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Update transaction stage:
            </p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="change-stage-btn">
                  Change Stage
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {STAGES.map((stage) => (
                  <DropdownMenuItem
                    key={stage.id}
                    onClick={() => onStageChange(stage.id)}
                    disabled={stage.id === normalizedStage}
                    data-testid={`stage-option-${stage.id}`}
                  >
                    {stage.label}
                    {stage.id === normalizedStage && (
                      <span className="ml-2 text-xs text-muted-foreground">(current)</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}
    </div>
  );
};
